import { keepPreviousData } from "@tanstack/react-query";

import { call, DEFAULT_BASIS, GRID_STALE_TIME, PricingError, type Basis, type GridRow, type Market } from "./api";
import { countryName, money, pct1, projectionMissing } from "./format";
import { stageTarget } from "./price";
import type { ProjectionMissing } from "./projection";
import { EVAL_KEY } from "./strategies";

// The basket scenarios (C1 v1.40, c1-scenarios, go-live unit 6, parent CL-023; companion of precision-pricing c8-scenarios, C8
// v1.3 with C2 v1.42). C2 prices every basket; the portal shows its strings and computes nothing (H12).

/** One order of `quantity` units, as C2 prices it: money as decimal strings in the store currency, null where an input is missing. */
export type Basket = {
  quantity: number; basket: string; free_shipping: boolean; shipping_paid: string | null; goods_ex_vat: string;
  shipping_ex_vat: string | null; revenue_ex_vat: string | null; cogs: string; pick_pack: string; packaging: string; courier: string;
  other: string | null; fulfilment: string; profit_per_order: string | null; profit_per_unit: string | null; margin_pct: string | null;
  missing: string[];
};
export type ScenarioCap = { crossing: number | null; last_quantity: number; ceiling: number; capped: boolean; rule: string };
export type ScenarioDefinition = { has_free_tier: boolean; threshold: string | null; standard_delivery_charge: string | null; currency: string };
export type Scenario = {
  xero_sku: string; product: string; variant: string; channel_location: string; market: Market; currency: string; cogs_basis: Basis;
  price: { value: string; currency: string; vat_inclusive: boolean }; countries: { country: string; orders: number }[];
  country: string | null; quantity: number; definition: ScenarioDefinition | null; vat_rate: string | null; stack: Basket | null;
  scan: Basket[]; worst: Basket | null; cap: ScenarioCap | null; missing: string[]; method: string; notes: string[];
};
export type ScenarioBody = { xero_sku: string; new_price: string; cogs_basis?: Basis; country?: string; quantity?: number };
export type WorstBasket = { country: string; quantity: number; free_shipping: boolean; profit_per_unit: string; margin_pct: string };
export type WorstRow = {
  xero_sku: string; product: string; variant: string; list_price: { value: string; currency: string } | null; worst: WorstBasket | null;
  countries_priced: number; countries_total: number; reason: string | null;
};
export type WorstBaskets = { market: Market; basis: Basis; rows: WorstRow[]; method: string };

/** The worst baskets sit under the evaluation's key: they are priced at the list price (the saved line, else the committed price),
 * so every save, remove and commit that re-reads the evaluation re-reads them too. */
export const SCENARIOS_KEY = [...EVAL_KEY, "scenarios"] as const;
export const getWorstBaskets = (market: Market, basis: Basis = DEFAULT_BASIS) =>
  call<WorstBaskets>(`/scenarios/${market}${basis === DEFAULT_BASIS ? "" : `?basis=${basis}`}`);
export function worstQuery(market: Market, basis: Basis = DEFAULT_BASIS) {
  return { queryKey: [...SCENARIOS_KEY, market, basis] as const, queryFn: () => getWorstBaskets(market, basis), retry: 1, staleTime: GRID_STALE_TIME };
}

export const runScenario = (body: ScenarioBody) => call<Scenario>("/scenarios", { method: "POST", body: JSON.stringify(body) });
/** The body for a Shopify row at the typed price: the store's SKU, the basis only when it is not the default (as `getGrid` asks), the
 * country only once one is picked (C2 answers with the ship country with the most orders) and the quantity only above one. */
export function scenarioBody(row: GridRow, price: string, basis: Basis = DEFAULT_BASIS, country: string | null = null, quantity = 1): ScenarioBody {
  return {
    ...stageTarget(row), new_price: price, ...(basis === DEFAULT_BASIS ? {} : { cogs_basis: basis }),
    ...(country === null ? {} : { country }), ...(quantity === 1 ? {} : { quantity }),
  };
}
export const SCENARIO_KEY = ["pricing", "scenario"] as const;
/** One answer per SKU, basis, price, country and quantity; never stale and never retried, as the projection (a refusal is an answer).
 * The last answer stays on screen while the next is worked out, so a price, country or quantity change never blanks the block. */
export function scenarioQuery(body: ScenarioBody) {
  return {
    queryKey: [...SCENARIO_KEY, body.xero_sku, body.cogs_basis ?? DEFAULT_BASIS, body.new_price, body.country ?? "", body.quantity ?? 1] as const,
    queryFn: () => runScenario(body), retry: 0, staleTime: 0, placeholderData: keepPreviousData,
  };
}

/** The quantities a person may pick: C2's ceiling (DEC-3 ruling C1). */
export const QUANTITIES = Array.from({ length: 24 }, (_, i) => i + 1);

export const SCENARIO_COPY = {
  title: "Basket scenarios",
  intro: "One order of several units at the typed price, single order. The free-shipping threshold makes profit per unit jump: this is where.",
  country: "Ship country",
  quantity: "Quantity",
  units: (n: number) => (n === 1 ? "1 unit" : `${n} units`),
  pending: "Working out the baskets…",
  updating: "Updating the baskets…",
  enter: "Enter a price above to see the baskets.",
  unavailable: "The baskets could not be worked out. Try again in a moment.",
  notPricedHere: "This row has no basket scenarios.",
  free: (threshold: string) => `reaches the ${threshold} threshold, so it ships free`,
  pays: (threshold: string, charge: string) => `below the ${threshold} threshold, so the customer pays ${charge}`,
  noTier: (charge: string) => `no free tier here, so the customer pays ${charge}`,
  noCharge: "no standard delivery charge is set for this country, so the shipping cannot be priced",
  basket: (value: string, how: string) => `Basket ${value}: ${how}`,
  revenue: (total: string | null, goods: string, shipping: string | null) =>
    `Revenue without VAT ${total ?? "cannot be worked out"} (goods ${goods}${shipping === null ? "" : `, shipping ${shipping}`})`,
  otherUnknown: "not yet available",
  costs: (cogs: string, pick: string, pack: string, courier: string, other: string | null) =>
    `COGS ${cogs} · Pick and pack ${pick} · Packaging ${pack} · Courier ${courier} · Other fees ${other ?? SCENARIO_COPY.otherUnknown}`,
  profit: (order: string, unit: string, margin: string) => `Profit ${order} per order, ${unit} per unit, ${margin} of revenue`,
  noProfit: "Profit cannot be worked out without the shipping charge",
  worst: (units: string, margin: string, unit: string) => `Worst basket: ${units} at ${margin} (${unit} per unit)`,
  noWorst: "No basket on the scan can be priced.",
  capped: "The threshold is not reached within 24 units, so the scan stops there.",
  scanHead: ["Quantity", "Basket", "Shipping", "Per unit", "Margin"] as const,
  shipFree: "Free",
  shipPaid: "Customer pays",
  shipUnknown: "Charge not set",
  worstChip: "Worst",
  missing: (reasons: string) => `Cannot be worked out: ${reasons}`,
  footer: (basis: string) => `${basis} basis. COGS and each fulfilment standard are per unit times the quantity; Precision Fulfilment Costs serves the courier per unit, so a larger parcel is not modelled. Today's fees.`,
  link: (units: string, margin: string, country: string) => `worst basket: ${units} at ${margin}, ${country}`,
  linkTitle: "Shopify profit per unit jumps at the free-shipping threshold. This is the basket with the lowest margin at the list price. Opens the basket scenarios in the row detail.",
};

/** The reasons only the scenarios name; the rest are the projection's words. */
export const SCENARIO_MISSING: Record<string, string> = {
  charge_not_set: "no standard delivery charge set in the Shipping pane",
  no_shipping_definition: "no shipping definition for this country",
  shipping_other_currency: "this country's shipping is held in another currency than the store's",
};
export function scenarioMissing(reason: string): string {
  return SCENARIO_MISSING[reason] ?? projectionMissing(reason as ProjectionMissing) ?? reason;
}

/** How the basket ships, in words: C2's free flag and the country's own threshold and charge, shown as served. */
export function shippingText(b: Basket, d: ScenarioDefinition | null, currency: string): string {
  if (b.free_shipping && d?.threshold != null) return SCENARIO_COPY.free(money(d.threshold, currency));
  if (b.shipping_paid === null) return SCENARIO_COPY.noCharge;
  const charge = money(b.shipping_paid, currency);
  return d?.has_free_tier && d.threshold != null ? SCENARIO_COPY.pays(money(d.threshold, currency), charge) : SCENARIO_COPY.noTier(charge);
}

/** The Price Run link of a store row: null with no worst basket (a reason C2 names stays in the drawer). */
export function worstLinkText(row: WorstRow | undefined): string | null {
  if (!row?.worst) return null;
  return SCENARIO_COPY.link(SCENARIO_COPY.units(row.worst.quantity), pct1(row.worst.margin_pct), countryName(row.worst.country));
}

/** A refusal of the scenario read, in words (AGENTS rule 14). */
export function scenarioRefusal(error: unknown): string {
  if (error instanceof PricingError) {
    const reason = error.reason();
    if (reason === "bad_price" || reason === "bad_request") return SCENARIO_COPY.enter;
    if (reason === "not_shopify" || reason === "inactive_sku" || reason === "unknown_sku" || reason === "not_priced_here") return SCENARIO_COPY.notPricedHere;
  }
  return SCENARIO_COPY.unavailable;
}
